import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { PhotoCamera, Delete, PhotoLibrary } from '@mui/icons-material';

export default function ImageUploader({ images: initialImages = [], onChange, onImagesSelected, maxImages = 5, locationOptions = [] }) {
  const [images, setImages] = useState(initialImages);
  const [lastInitialLength, setLastInitialLength] = useState(initialImages.length);
  
  // Update images when initialImages change (z.B. beim Laden eines Entwurfs)
  // Aber nur wenn sich die LÄNGE ändert (verhindert Infinite Loop)
  useEffect(() => {
    if (initialImages.length !== lastInitialLength) {
      console.log('🖼️ ImageUploader: initialImages geändert', initialImages.length, 'Bilder');
      setImages(initialImages);
      setLastInitialLength(initialImages.length);
    }
  }, [initialImages.length]); // NUR auf Länge reagieren, nicht auf das Array selbst
  
  // Support both onChange and onImagesSelected props
  const handleImagesChange = (updatedImages) => {
    setImages(updatedImages);
    if (onChange) {
      onChange(updatedImages);
    }
    if (onImagesSelected) {
      onImagesSelected(updatedImages);
    }
  };
  
  // Hilfsfunktion zur Bildkomprimierung
  const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Berechne neue Dimensionen unter Beibehaltung des Seitenverhältnisses
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          // Erstelle Canvas und zeichne das Bild in neuer Größe
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Konvertiere zu Base64 mit reduzierter Qualität
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // Berechne Kompressionsrate
          const originalSize = (event.target.result.length * 0.75) / 1024; // KB
          const compressedSize = (compressedDataUrl.length * 0.75) / 1024; // KB
          const reduction = ((1 - compressedSize / originalSize) * 100).toFixed(1);
          
          console.log(`🖼️ Bild komprimiert: ${originalSize.toFixed(0)} KB → ${compressedSize.toFixed(0)} KB (${reduction}% kleiner)`);
          
          resolve({
            data: compressedDataUrl,
            originalSize: originalSize,
            compressedSize: compressedSize
          });
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };
  
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > maxImages) {
      alert(`Maximal ${maxImages} Bilder erlaubt`);
      return;
    }

    // Zeige Lade-Indikator
    console.log(`📸 Komprimiere ${files.length} Bild(er)...`);
    
    for (const file of files) {
      try {
        // Komprimiere das Bild
        const compressed = await compressImage(file);
        
        const newImage = {
          name: file.name,
          data: compressed.data,
          mime_type: 'image/jpeg', // Immer JPEG nach Komprimierung
          description: '',
          originalSize: compressed.originalSize,
          compressedSize: compressed.compressedSize
        };
        
        const updatedImages = [...images, newImage];
        handleImagesChange(updatedImages);
      } catch (error) {
        console.error('❌ Fehler beim Komprimieren:', error);
        alert(`Fehler beim Verarbeiten von ${file.name}`);
      }
    }
  };

  const handleDescriptionChange = (index, description) => {
    const updatedImages = [...images];
    updatedImages[index].description = description;
    handleImagesChange(updatedImages);
  };

  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    handleImagesChange(updatedImages);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Bilder ({images.length}/{maxImages})
        </Typography>
        <ButtonGroup variant="contained" disabled={images.length >= maxImages}>
          {/* Kamera Button - öffnet direkt die Kamera */}
          <Button
            component="label"
            startIcon={<PhotoCamera />}
            disabled={images.length >= maxImages}
          >
            Kamera
            <input
              type="file"
              hidden
              accept="image/*"
              capture="environment"
              onChange={handleImageUpload}
            />
          </Button>
          
          {/* Galerie Button - öffnet Galerie/Dateiauswahl */}
          <Button
            component="label"
            startIcon={<PhotoLibrary />}
            disabled={images.length >= maxImages}
          >
            Galerie
            <input
              type="file"
              hidden
              accept="image/*"
              multiple
              onChange={handleImageUpload}
            />
          </Button>
        </ButtonGroup>
      </Box>

      {images.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Keine Bilder hochgeladen
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {images.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardMedia
                  component="img"
                  height="140"
                  image={image.data}
                  alt={image.name}
                />
                <CardContent>
                  <Typography variant="caption" display="block" gutterBottom>
                    {image.name}
                  </Typography>
                  {locationOptions && locationOptions.length > 0 ? (
                    <FormControl fullWidth size="small">
                      <InputLabel>Montageort</InputLabel>
                      <Select
                        value={image.description || ''}
                        onChange={(e) => handleDescriptionChange(index, e.target.value)}
                        label="Montageort"
                      >
                        <MenuItem value="">
                          <em>Kein Ort ausgewählt</em>
                        </MenuItem>
                        {locationOptions.map((option, i) => (
                          <MenuItem key={i} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      label="Hinweistext"
                      value={image.description}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      placeholder="Optional - Hinweistext zum Foto"
                    />
                  )}
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveImage(index)}
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}